// apiClient.ts - Updated to use centralized configuration

import axios from 'axios';
import keycloak, { terminateKeycloakSession } from '../keycloak';
import { getBaseUrl, getApiConfig } from '../config/apiConfig';
import { getKeycloakRefreshMinValiditySeconds, isKeycloakEnabled } from '../config/authConfig';
import authService from './authService';

const isFormDataPayload = (data: unknown): data is FormData =>
  typeof FormData !== 'undefined' && data instanceof FormData;

const removeContentTypeHeader = (headers: any) => {
  if (!headers) return;
  if (typeof headers.delete === 'function') {
    headers.delete('Content-Type');
    headers.delete('content-type');
    return;
  }
  delete headers['Content-Type'];
  delete headers['content-type'];
};

// Get configuration from centralized config
const apiConfig = getApiConfig();

// Create axios instance with centralized configuration
const apiClient = axios.create({
  baseURL: getBaseUrl(),
  timeout: apiConfig.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Attach authentication token conditionally
apiClient.interceptors.request.use(
  async (config) => {
    (config as any).__dmsRequestStartedAt = Date.now();

    if (isFormDataPayload(config.data)) {
      removeContentTypeHeader(config.headers);
    }

    if (isKeycloakEnabled()) {
      if (keycloak?.authenticated && typeof keycloak.updateToken === 'function') {
        try {
          await keycloak.updateToken(getKeycloakRefreshMinValiditySeconds());
        } catch (refreshError) {
          await terminateKeycloakSession('refresh-before-request-failed');
          return Promise.reject(refreshError);
        }
      }

      // Keycloak mode: attach refreshed token
      if (keycloak?.authenticated && keycloak.token) {
        config.headers['Authorization'] = `Bearer ${keycloak.token}`;
      }
    } else {
      // Non-Keycloak mode: attach backend JWT token
      const token = authService.getValidToken(true);
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: Handle common errors and retries
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config ?? {};
    const status = error.response?.status;
    
    // Only 401 indicates an expired/invalid session. Business-rule 403 responses
    // must surface to the caller without forcing a logout.
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      if (!isKeycloakEnabled()) {
        // Non-Keycloak mode: clear auth and redirect to login
        console.log(`🔓 Authentication expired (HTTP ${status}) - clearing auth data`);
        authService.handleAuthFailure({ redirectToLogin: true, reason: `http-${status}` });
      } else if (keycloak?.authenticated && typeof keycloak.updateToken === 'function') {
        try {
          await keycloak.updateToken(getKeycloakRefreshMinValiditySeconds());
          if (keycloak.token) {
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers['Authorization'] = `Bearer ${keycloak.token}`;
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          console.warn('Keycloak token refresh failed after 401, logging out', refreshError);
          await terminateKeycloakSession('refresh-after-401-failed');
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;

// Export the base URL for backward compatibility
export const SERVER_URL = getBaseUrl();
